import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../../components/layout/Layout';
import Pagination from '../../components/ui/Pagination';
import Modal from '../../components/ui/Modal';
import api from '../../api/axios';

const DEPARTMENTS = ['Administration', 'Engineering', 'Finance', 'HR', 'IT', 'Marketing', 'Operations', 'Sales', 'Security', 'Other'];

function fmt(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function AttendanceRecords() {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ department: '', date: '', status: '' });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const limit = 20;

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit });
      if (filters.department) params.append('department', filters.department);
      if (filters.date) params.append('date', filters.date);
      if (filters.status) params.append('status', filters.status);
      const res = await api.get(`/attendance/all?${params}`);
      setRecords(res.data.attendance);
      setTotal(res.data.total);
    } catch {}
    setLoading(false);
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  function exportCSV() {
    const params = new URLSearchParams();
    if (filters.department) params.append('department', filters.department);
    if (filters.date) params.append('date', filters.date);
    if (filters.status) params.append('status', filters.status);
    const token = localStorage.getItem('token');
    window.open(`${API_BASE}/api/attendance/export?${params}&token=${token}`, '_blank');
  }

  function updateFilter(key, val) {
    setFilters(f => ({ ...f, [key]: val }));
    setPage(1);
  }

  return (
    <Layout>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1>Attendance Records</h1>
          <p>View and export all staff attendance</p>
        </div>
        <button className="btn btn-success" onClick={exportCSV}>⬇ Export CSV</button>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <select className="form-input" style={{ width: 180 }} value={filters.department} onChange={e => updateFilter('department', e.target.value)}>
          <option value="">All Departments</option>
          {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <input type="date" className="form-input" style={{ width: 180 }} value={filters.date} onChange={e => updateFilter('date', e.target.value)} />
        <select className="form-input" style={{ width: 150 }} value={filters.status} onChange={e => updateFilter('status', e.target.value)}>
          <option value="">All Statuses</option>
          <option value="present">Present</option>
          <option value="late">Late</option>
          <option value="absent">Absent</option>
        </select>
        <button className="btn btn-secondary" onClick={() => { setFilters({ department: '', date: '', status: '' }); setPage(1); }}>Clear</button>
      </div>

      <div className="card">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}><span className="spinner spinner-dark" /></div>
        ) : records.length === 0 ? (
          <div className="empty-state"><div style={{ fontSize: '3rem' }}>📋</div><p>No records found</p></div>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Staff</th><th>Department</th><th>Date</th><th>Sign In</th><th>Sign Out</th><th>Status</th><th>Photo</th><th>Location</th></tr>
                </thead>
                <tbody>
                  {records.map(r => (
                    <tr key={r.id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.full_name}</div>
                        <div style={{ color: '#64748b', fontSize: '0.8rem' }}>{r.staff_id}</div>
                      </td>
                      <td>{r.department}</td>
                      <td>{new Date(r.date).toLocaleDateString()}</td>
                      <td>{fmt(r.sign_in_time)}</td>
                      <td>{fmt(r.sign_out_time)}</td>
                      <td><span className={`badge badge-${r.status}`}>{r.status}</span></td>
                      <td>
                        {r.sign_in_photo ? (
                          <img
                            src={`${API_BASE}${r.sign_in_photo}`}
                            alt="Sign-in"
                            style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', cursor: 'pointer' }}
                            onClick={() => setSelected(r)}
                          />
                        ) : '—'}
                      </td>
                      <td>
                        {r.sign_in_latitude ? (
                          <button className="btn btn-sm btn-outline" onClick={() => setSelected(r)}>View Map</button>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} total={total} limit={limit} onPageChange={setPage} />
          </>
        )}
      </div>

      {selected && (
        <Modal title={`${selected.full_name} — ${new Date(selected.date).toLocaleDateString()}`} onClose={() => setSelected(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            {selected.sign_in_photo && (
              <div>
                <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600 }}>SIGN-IN PHOTO</p>
                <img src={`${API_BASE}${selected.sign_in_photo}`} alt="Sign-in" style={{ width: '100%', borderRadius: 8 }} />
              </div>
            )}
            {selected.sign_out_photo && (
              <div>
                <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 600 }}>SIGN-OUT PHOTO</p>
                <img src={`${API_BASE}${selected.sign_out_photo}`} alt="Sign-out" style={{ width: '100%', borderRadius: 8 }} />
              </div>
            )}
          </div>

          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '1rem', fontSize: '0.875rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
              <div><span style={{ color: '#64748b' }}>Sign In:</span> <strong>{fmt(selected.sign_in_time)}</strong></div>
              <div><span style={{ color: '#64748b' }}>Sign Out:</span> <strong>{fmt(selected.sign_out_time)}</strong></div>
              <div style={{ gridColumn: '1/-1' }}><span style={{ color: '#64748b' }}>Location:</span> {selected.sign_in_address || `${selected.sign_in_latitude}, ${selected.sign_in_longitude}`}</div>
            </div>
          </div>

          {selected.sign_in_latitude && (
            <a
              href={`https://www.openstreetmap.org/?mlat=${selected.sign_in_latitude}&mlon=${selected.sign_in_longitude}&zoom=16`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline btn-block"
              style={{ marginTop: '1rem' }}
            >
              🗺 Open in OpenStreetMap
            </a>
          )}
        </Modal>
      )}
    </Layout>
  );
}
